import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  UseQueryOptions,
  UseMutationOptions,
  UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query';
import { useAuthContext } from './contexts.tsx';
import { useTenantContext } from './contexts.tsx';
import { useCallback } from 'react';
import { apiQueryKey } from './queryKeys.ts';
import { toQueryState, type QueryState } from './queryState.ts';
import {
  createAuthenticatedFetch,
  type AuthenticatedFetchInit,
} from './apiFetchAuthWithRefresh.ts';
import { toAuthAwareApiError } from './apiErrorShape.ts';
import { normalizeListEnvelope } from './listEnvelope.ts';
import {
  buildRetryOptions,
  type RetryConfig,
  type RetryStrategyName,
} from './retryConfig.ts';
import {
  appendQuery,
  pageAdapter,
  type PageEnvelope,
  type PaginationAdapter,
  type CursorEnvelope,
  cursorAdapter,
} from './pagination.ts';

/**
 * Error response from API.
 *
 * Still an interface, so every `UseQueryOptions<T, ApiError>` /
 * `error.statusCode` call site is unchanged — but the value actually thrown is
 * now an {@link ApiRequestError}, a real `Error` subclass with these fields.
 * That is what makes `error instanceof Error` true, and therefore what makes
 * `ErrorState` (and every hand-written `instanceof Error ? … : 'Unknown
 * error'` ternary) render the server's stated reason instead of a generic
 * fallback.
 */
export interface ApiError {
  message: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, unknown>;
}

// Re-exported from its own hook-free module so it stays unit-testable in
// isolation; the public `@tyndo/hooks` surface is unchanged (still exported here
// and via the index barrel). Callers hand the raw thrown value to this helper
// instead of the instanceof dance — it handles both the thrown
// `ApiRequestError` and any raw ProblemDetails body a caller passes directly.
export { extractErrorMessage } from './extractErrorMessage.ts';
export { ApiRequestError, isApiRequestError } from './apiErrorShape.ts';

/**
 * Response wrapper for paginated endpoints.
 *
 * Kept as a concrete alias of the shared {@link PageEnvelope} so the long-lived
 * public type name stays stable for the many call sites importing it, while the
 * envelope variance (offset / cursor) lives in `./pagination` (A5-440).
 */
export type PaginatedResponse<T> = PageEnvelope<T>;

/**
 * Extension of the authenticated fetch init exposed to query/mutation callers.
 * `timeoutMs` overrides the default 30 s client timeout — required for CPU-only
 * LLM round-trips that take 60–180 s on the operator workstation.
 */
type FetchOptions = AuthenticatedFetchInit;

/**
 * Wrap the shared {@link createAuthenticatedFetch} with the JSON decode +
 * `ApiError`-throwing semantics this hook layer relies on (call sites branch on
 * `error.statusCode`). The network + auth + silent-refresh path itself lives in
 * `apiFetchAuthWithRefresh` and is shared verbatim with `useApiClient` (A5-441).
 */
const createFetchWithAuth =
  (token: string | null, tenantId: string | null) => {
    const authedFetch = createAuthenticatedFetch(token, tenantId);
    return async (url: string, options: FetchOptions = {}) => {
      // Default JSON content-type for string-bodied requests, matching the
      // prior behavior; the shared fetch only defaults it when `json` is used,
      // but these hooks pass a pre-stringified `body`. A FormData / Blob /
      // ArrayBuffer body must NOT get a forced JSON content-type — the browser
      // sets the multipart boundary itself, and an octet-stream caller supplies
      // its own type (A5-443).
      const callerHeaders = options.headers as Record<string, string> | undefined;
      const isStructuredBody =
        typeof FormData !== 'undefined' && options.body instanceof FormData;
      const isBinaryBody =
        (typeof Blob !== 'undefined' && options.body instanceof Blob) ||
        options.body instanceof ArrayBuffer ||
        ArrayBuffer.isView(options.body as ArrayBufferView);
      const headers: Record<string, string> =
        isStructuredBody || isBinaryBody
          ? { ...callerHeaders }
          : { 'Content-Type': 'application/json', ...callerHeaders };
      const response = await authedFetch(url, { ...options, headers });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Precedence message → error → detail → title → statusText covers the
        // hooks' own ApiError shape, the legacy `new { error = ... }` module
        // envelope, and RFC-7807 ProblemDetails (H-1). See apiErrorShape.ts.
        // 401/403 go through the SAME extraction (they used to throw before
        // the body was parsed, so an SoD ProblemDetails explanation collapsed
        // to a generic "Forbidden"); the auth-aware wrapper preserves the
        // public contract that `code` is 'unauthorized'/'forbidden' when the
        // body supplies no code of its own.
        const error: ApiError = toAuthAwareApiError(
          errorData,
          response.status,
          response.statusText,
        );
        throw error;
      }
      const ct = response.headers.get('content-type') ?? '';
      if (ct && !ct.includes('application/json')) {
        // Non-JSON 2xx (e.g. 204) — return undefined rather than throwing on
        // an empty body parse.
        return undefined;
      }
      // ONE list contract for every view. The gateway returns lists in two
      // shapes — a bare array, or `{__source, __freshAt, data: [...]}` from
      // WithProvenance.Of(...) — and nothing on the wire says which. Views that
      // guessed wrong died with "…map is not a function" and took the whole
      // pane white. Normalising HERE, at the single fetch funnel, means no view
      // ever branches on shape; provenance survives as non-enumerable fields on
      // the returned array. Paginated envelopes pass through untouched — see
      // listEnvelope.ts.
      return normalizeListEnvelope(await response.json());
    };
  };

/**
 * Hook for fetching data using TanStack Query.
 *
 * Request deduplication (A5-442): two components calling `useApiQuery` with the
 * SAME endpoint (and tenant) share a single in-flight request — TanStack dedups
 * by query key, returning the same in-flight promise to every observer
 * regardless of `staleTime`. Because every request is keyed via `apiQueryKey`
 * (tenant + resource + filters), identical reads never double the backend load;
 * distinct tenants/filters correctly issue separate requests. See
 * `requestDedup.test.ts` for the executable contract.
 *
 * @template T - The expected response data type
 * @param endpoint - API endpoint path (relative to base URL)
 * @param options - TanStack Query options
 * @returns Query result with data, isLoading, error, etc.
 *
 * @throws {ApiError} The `queryFn` rejects with an {@link ApiRequestError} — a
 *   real `Error` subclass carrying the {@link ApiError} fields, so
 *   `error instanceof Error` narrows and `error.message` already holds the
 *   server's stated reason; branch on `error.statusCode`/`error.code`
 *   on any 4xx/5xx gateway response: 401 (`unauthorized`), 403 (`forbidden`), or
 *   any other non-2xx. Surfaced via the query result's `error`/`isError`, not a
 *   synchronous throw from the hook itself.
 * @throws {Error} The `queryFn` rejects with a DOMException/`Error` on a network
 *   failure, a request timeout, or an `AbortSignal` abort (unmount / cancelled
 *   query). Also surfaced via `error`/`isError`.
 *
 * @example
 * const { data, isLoading } = useApiQuery<User>('/users/me');
 */
export const useApiQuery = <T,>(
  endpoint: string,
  options?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>
) => {
  const { token } = useAuthContext();
  const { tenantId } = useTenantContext();

  const fetchFn = createFetchWithAuth(token, tenantId);

  return useQuery<T, ApiError>({
    // Tenant-safe hierarchical key: ['api','v1', tenantId, ...segments, filters].
    // Hoisting tenantId ahead of the resource segments makes every resource
    // prefix tenant-scoped, so a prefix-matched invalidation can never cross
    // the tenant boundary (A5-023). `token` is deliberately NOT in the key: a
    // token refresh must not orphan the cache and trigger a refetch storm — the
    // current token is read at fetch time via the `fetchFn` closure.
    queryKey: apiQueryKey(endpoint, tenantId),
    // Forward TanStack Query's per-query AbortSignal so a cancelled/unmounted
    // query actually aborts the in-flight gateway request.
    queryFn: ({ signal }) => fetchFn(endpoint, { signal }),
    // Skip the request when no endpoint is provided (e.g. master/detail
    // panes that haven't selected a parent yet). Caller-supplied `enabled`
    // overrides this default. Without it, an empty path collapses to the
    // bare `/api/v1` prefix and the gateway returns 404.
    enabled: Boolean(endpoint),
    ...options,
  });
};

/**
 * `useApiQuery` plus a canonical {@link QueryState} discriminant (A5-195).
 *
 * Returns the full TanStack result (so existing call sites keep working) with
 * an added `state` field that collapses the isPending/isError/data/isFetching/
 * dataUpdatedAt flag bag into one exhaustive union — idle / pending / success
 * (with freshness + isStale + isEmpty) / error (with last-known-good). Switch on
 * `state.status` for consistent skeleton-vs-gray-out-vs-empty-vs-error rendering
 * instead of re-deriving it per component.
 *
 * @throws {ApiError} on a 4xx/5xx gateway response (surfaced via `state.error`).
 */
export const useApiQueryState = <T,>(
  endpoint: string,
  options?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>,
): ReturnType<typeof useApiQuery<T>> & { state: QueryState<T> } => {
  const result = useApiQuery<T>(endpoint, options);
  const state = toQueryState<T>({
    data: result.data,
    error: result.error,
    isError: result.isError,
    isPending: result.isPending,
    isFetching: result.isFetching,
    fetchStatus: result.fetchStatus,
    dataUpdatedAt: result.dataUpdatedAt,
  });
  return { ...result, state };
};

/**
 * `useApiQuery` with an explicit, named retry/backoff strategy (A5-438).
 *
 * Plain `useApiQuery` inherits TanStack's defaults (3 retries, built-in
 * exponential backoff) and applies them uniformly — wrong for a slow historian
 * aggregation (retrying piles on load) and wrong for a regulated read (which
 * must surface the error immediately). This wrapper compiles a strategy into
 * the `{ retry, retryDelay }` TanStack consumes, using full-jitter backoff and
 * a status-aware `shouldRetry` (never retries 401/403/deterministic-4xx; retries
 * transient 5xx/408/429 and network errors). See `retryConfig.ts` for the
 * `realtime` / `standard` / `historian` / `regulated` tiers; pass a name or a
 * custom {@link RetryConfig}. Caller-supplied `retry`/`retryDelay` in `options`
 * still win (last spread).
 *
 * @throws {ApiError} on a 4xx/5xx gateway response (surfaced via `error`); the
 *   strategy's `shouldRetry` never retries 401/403/deterministic 4xx and retries
 *   transient 5xx/408/429 before surfacing the final {@link ApiError}.
 * @throws {Error} on a network failure / timeout / abort (surfaced via `error`),
 *   retried per the chosen strategy then surfaced.
 *
 * @example
 *   // Live PLC tile — retry transient edge-sync 503s quickly.
 *   const q = useApiQueryWithRetry<TagSnapshot>('/scada/tags/T-1', 'realtime');
 *   // Slow historian range — retry once, slowly.
 *   const h = useApiQueryWithRetry<Series>('/historian/range?...', 'historian');
 */
export const useApiQueryWithRetry = <T,>(
  endpoint: string,
  strategy: RetryStrategyName | RetryConfig = 'standard',
  options?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>,
): ReturnType<typeof useApiQuery<T>> => {
  const retryOptions = buildRetryOptions(strategy);
  return useApiQuery<T>(endpoint, {
    ...retryOptions,
    ...options,
  });
};

/**
 * Descriptor passed to `mutate()` when `useApiMutation` is called with no
 * arguments — the legacy "do everything per call" pattern many module views
 * still use.
 *
 * @deprecated Use the endpoint-bound form `useApiMutation(endpoint, method)`
 * instead. The descriptor form carries the method+url at mutate time, so the
 * hook has no stable endpoint to key cache invalidation off, and TanStack
 * cannot dedup or scope it per resource. Slated for removal in v1.0.
 *
 * ### Migration
 * ```diff
 * - const { mutate } = useApiMutation();
 * - mutate({ method: 'POST', url: '/alarms/ack', data: { ids } });
 * + const { mutate } = useApiMutation('/alarms/ack', 'POST');
 * + mutate({ ids });
 * ```
 * When a single component issues writes to several distinct endpoints, declare
 * one endpoint-bound `useApiMutation(...)` per endpoint (hooks are cheap) rather
 * than one descriptor-mode hook multiplexing across urls. After migrating, the
 * `onSuccess` handler can invalidate the exact resource via
 * `invalidateApiQueries(queryClient, tenantId, '/alarms')`.
 */
export interface ApiMutationDescriptor<TData = unknown> {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | string;
  url: string;
  data?: TData;
}

/**
 * Hook for mutations (POST, PUT, DELETE) using TanStack Query.
 *
 * Two calling shapes are supported:
 *
 * 1. **Endpoint-bound** (preferred) — the hook is bound to a single endpoint
 *    and HTTP method; `.mutate(variables)` sends `variables` as the body.
 *    ```tsx
 *    const { mutate } = useApiMutation<User, CreateUserInput>('/users', 'POST');
 *    mutate({ name: 'John' });
 *    ```
 *
 * 2. **Per-call descriptor** (legacy, `@deprecated`) — call with no arguments
 *    and pass a `{ method, url, data }` descriptor at mutate time. Used by
 *    several module views authored against an earlier API. Prefer shape 1: the
 *    descriptor form has no stable endpoint to key invalidation off and is
 *    slated for removal in v1.0 (see {@link ApiMutationDescriptor}).
 *    ```tsx
 *    const { mutate } = useApiMutation();
 *    mutate({ method: 'POST', url: '/alarms/ack', data: { ids } });
 *    ```
 *
 * The two shapes are differentiated at runtime by whether `endpoint` is
 * provided.
 *
 * @throws {ApiError} The mutation rejects with an {@link ApiRequestError} (an
 *   `Error` subclass — branch on `error.statusCode`/`error.code`) on any 4xx/5xx response:
 *   401 (`unauthorized`), 403 (`forbidden`), or any other non-2xx. Caught via
 *   `onError`/`mutateAsync().catch`, or read off the result's `error`/`isError`.
 * @throws {Error} The mutation rejects with an `Error`/DOMException on a network
 *   failure, a timeout (default 30 s JSON / 120 s file; override with
 *   `timeoutMs`), or abort. Same propagation as above.
 */
/**
 * Tyndo-specific options that can be threaded into useApiMutation alongside
 * the TanStack Query options. Use `timeoutMs` to override the default 30 s
 * client-side fetch timeout — required for slow LLM round-trips (60–180 s on
 * CPU) and any other long-running endpoint.
 */
export interface ApiMutationExtraOptions {
  timeoutMs?: number;
  /**
   * Wire format for the request body (A5-443):
   *   - `'json'` (default)         — `variables` are JSON-stringified.
   *   - `'multipart/form-data'`    — `variables` are a `FormData`; the browser
   *                                  sets the multipart boundary itself.
   *   - `'application/octet-stream'` — `variables` are a `Blob`/`ArrayBuffer`;
   *                                  sent raw with that content-type.
   *
   * Use this to put file uploads (Vision images, CAD, evidence PDFs) through
   * the normal mutation lifecycle (isPending / onSuccess / onError) instead of
   * the imperative `useApiClient.upload()`.
   */
  mediaType?: 'json' | 'multipart/form-data' | 'application/octet-stream';
}

// Overload 1: endpoint-bound. TVariables defaults to `unknown` (not `void`)
// because most call sites send an object body but don't supply explicit
// generics — `void` would force them to do so.
export function useApiMutation<TData = unknown, TVariables = unknown>(
  endpoint: string,
  method?: 'POST' | 'PUT' | 'DELETE',
  options?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'> & ApiMutationExtraOptions
): ReturnType<typeof useMutation<TData, ApiError, TVariables>>;
/**
 * Overload 2: per-call descriptor (legacy).
 *
 * @deprecated Prefer the endpoint-bound overload `useApiMutation(endpoint, method)`.
 * See {@link ApiMutationDescriptor} for the migration diff. Slated for removal
 * in v1.0; a codemod (`@tyndo/hooks` migrate script) converts descriptor call
 * sites to endpoint-bound by lifting `{ method, url }` into the hook arguments.
 */
export function useApiMutation<TData = unknown>(): ReturnType<
  typeof useMutation<TData, ApiError, ApiMutationDescriptor>
>;
// Implementation
export function useApiMutation<TData = unknown, TVariables = unknown>(
  endpoint?: string,
  method: 'POST' | 'PUT' | 'DELETE' = 'POST',
  options?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'> & ApiMutationExtraOptions
) {
  const { token } = useAuthContext();
  const { tenantId } = useTenantContext();

  const fetchFn = createFetchWithAuth(token, tenantId);
  const mediaType = options?.mediaType ?? 'json';
  // Non-JSON transfers (file upload/download) get a larger default timeout so a
  // multi-MB Vision image isn't aborted at 30 s; an explicit timeoutMs wins.
  const timeoutMs =
    options?.timeoutMs ?? (mediaType === 'json' ? undefined : 120_000);

  const mutationFn = useCallback(
    (variables: TVariables | ApiMutationDescriptor) => {
      // Descriptor mode: caller supplied no endpoint, so each .mutate() call
      // carries its own method/url/data. (Descriptor mode is JSON-only.)
      if (endpoint === undefined) {
        const desc = variables as ApiMutationDescriptor;
        const m = (desc.method ?? 'POST').toUpperCase();
        const body =
          m === 'GET' || m === 'DELETE' || desc.data === undefined
            ? undefined
            : JSON.stringify(desc.data);
        return fetchFn(desc.url, { method: m, body, timeoutMs });
      }
      // Endpoint-bound mode. For non-JSON media types the variables ARE the
      // raw body (FormData / Blob / ArrayBuffer); createFetchWithAuth skips the
      // JSON content-type so the browser sets the multipart boundary (A5-443).
      let body: BodyInit | undefined;
      if (method === 'DELETE') {
        body = undefined;
      } else if (mediaType === 'json') {
        body = JSON.stringify(variables);
      } else {
        body = variables as unknown as BodyInit;
      }
      return fetchFn(endpoint, { method, body, timeoutMs });
    },
    [endpoint, method, fetchFn, timeoutMs, mediaType]
  );

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: mutationFn as (vars: TVariables) => Promise<TData>,
    ...options,
  });
}

/**
 * File-mutation convenience over {@link useApiMutation} (A5-443).
 *
 * Wraps a `multipart/form-data` (default) or `application/octet-stream` upload
 * in the full mutation lifecycle so a file upload gets `isPending` / `onSuccess`
 * / `onError` like any other write — replacing the imperative
 * `useApiClient.upload()` for the common case. `mutate(formData)` (or a Blob for
 * octet-stream) sends the body raw; the server's JSON response is returned.
 *
 * For uploads needing byte-level progress, keep using `useApiClient.upload()`
 * (XHR-based) — the fetch lifecycle here exposes pending/settled, not progress.
 *
 * @throws {ApiError} on a 4xx/5xx response (rejected mutation; via `onError` or
 *   `error`/`isError`) — e.g. 413 payload-too-large, 415 unsupported-media-type.
 * @throws {Error} on a network failure / upload timeout (120 s default) / abort.
 *
 * @example
 *   const upload = useApiFileMutation<UploadResult>('/vision/datasets/123/images');
 *   const fd = new FormData();
 *   fd.append('file', file);
 *   upload.mutate(fd);
 */
export function useApiFileMutation<TData = unknown, TVariables extends BodyInit = FormData>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'> &
    Omit<ApiMutationExtraOptions, 'mediaType'> & {
      method?: 'POST' | 'PUT';
      mediaType?: 'multipart/form-data' | 'application/octet-stream';
    },
): ReturnType<typeof useMutation<TData, ApiError, TVariables>> {
  const { method = 'POST', mediaType = 'multipart/form-data', ...rest } = options ?? {};
  return useApiMutation<TData, TVariables>(endpoint, method, {
    ...rest,
    mediaType,
  });
}

/**
 * Hook for infinite/paginated queries using TanStack Query.
 *
 * Envelope-aware (A5-440): the page shape is no longer hard-wired to
 * `PaginatedResponse` (`{ data, hasMore, page }`). Pass a {@link PaginationAdapter}
 * to read endpoints that return `{ items, offset, limit, totalCount }` (use
 * `offsetAdapter()`) or any other shape; the default is `pageAdapter()`, which
 * preserves the original `?page=N` + `hasMore` behavior, so existing call sites
 * are unchanged.
 *
 * @template T        - The item type in the paginated list.
 * @template TEnvelope - The page envelope shape (defaults to PaginatedResponse).
 * @template PageParam - The page-cursor type (number for page/offset).
 *
 * @throws {ApiError} a page fetch rejects with an {@link ApiError} on any
 *   4xx/5xx gateway response; surfaced via `error`/`isError` (or
 *   `fetchNextPage()`'s rejection for a failed subsequent page).
 * @throws {Error} a page fetch rejects with an `Error` on a network failure /
 *   timeout / abort; same propagation.
 *
 * @example
 * // Default page-based:
 * const { data, fetchNextPage } = useApiInfiniteQuery<Product>('/products?pageSize=20');
 * @example
 * // Offset-based historian range:
 * const q = useApiInfiniteQuery('/historian/range?tag=T1', offsetAdapter<Sample>(200));
 */
export const useApiInfiniteQuery = <
  T,
  TEnvelope = PaginatedResponse<T>,
  PageParam = number,
>(
  endpoint: string,
  adapter: PaginationAdapter<TEnvelope, T, PageParam> = pageAdapter<T>() as unknown as PaginationAdapter<
    TEnvelope,
    T,
    PageParam
  >,
  options?: Omit<
    UseInfiniteQueryOptions<TEnvelope, ApiError>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >
) => {
  const { token } = useAuthContext();
  const { tenantId } = useTenantContext();

  const fetchFn = createFetchWithAuth(token, tenantId);

  // The TanStack v5 inference of TData differs between the caller-supplied
  // options bag (which may include a `select`) and the local options object
  // built here. Casting at the call site sidesteps the variance and keeps
  // both surfaces honest — callers still see the public signature above.
  return useInfiniteQuery({
    // See useApiQuery: tenant-safe hierarchical key; token is read at fetch
    // time, not part of the cache key.
    queryKey: apiQueryKey(endpoint, tenantId),
    queryFn: ({ pageParam, signal }) =>
      fetchFn(appendQuery(endpoint, adapter.toQueryFragment(pageParam as PageParam)), {
        signal,
      }),
    initialPageParam: adapter.initialPageParam,
    getNextPageParam: (lastPage: TEnvelope, _all, lastPageParam) =>
      adapter.getNextPageParam(lastPage, lastPageParam as PageParam),
    ...options,
  } as UseInfiniteQueryOptions<TEnvelope, ApiError>) as UseInfiniteQueryResult<
    InfiniteData<TEnvelope>,
    ApiError
  >;
};

/**
 * Cursor-based infinite query (A5-440) for endpoints returning
 * `{ results, cursor }` — event streams and unbounded genealogy walks where a
 * numeric offset is meaningless. Thin wrapper over {@link useApiInfiniteQuery}
 * with the {@link cursorAdapter}; advances via the opaque `cursor` token until
 * the server stops returning one.
 *
 * @throws {ApiError} a page fetch rejects with an {@link ApiError} on any
 *   4xx/5xx gateway response; surfaced via `error`/`isError`.
 * @throws {Error} a page fetch rejects with an `Error` on a network failure /
 *   timeout / abort; same propagation.
 *
 * @example
 * const q = useApiCursorQuery<AuditEvent>('/audit/events?entity=Lot-1');
 * // q.data.pages.flatMap(p => p.results)
 */
export const useApiCursorQuery = <T,>(
  endpoint: string,
  options?: Omit<
    UseInfiniteQueryOptions<CursorEnvelope<T>, ApiError>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >
) =>
  useApiInfiniteQuery<T, CursorEnvelope<T>, string | undefined>(
    endpoint,
    cursorAdapter<T>(),
    options,
  );
